import { openMailto } from "@/utils/mailto";
import Hero from "../components/hero";
import heroImage from "@assets/images/pay-bill-hero.svg";
import arrowRightIcon from "@assets/icons/arrow-narrow-right-gray.svg";
import paymentCardIcon from "@assets/icons/card.svg";
import bankIcon from "@assets/icons/bank.svg";
import mobileIcon from "@assets/icons/mobile.svg";
import autoDebitIcon from "@assets/icons/autodebit.svg";
import billImage from "@assets/images/sample-bill.svg";
import safeIcon from "@assets/icons/safeIcon.svg";
import Help from "../components/help";
import Button from "../components/button";
import { payBillSteps } from "../data";

const heroData = {
  title: "Manage Your Gas Account Anywhere.",
  description:
    "Pay bills, check usage, submit meter readings, or update your account all from one secure and convenient online platform.",
  image: heroImage,
  buttonText: "Quick Pay",
  buttonOnclick: () =>
    openMailto({
      subject: "Bill Payment Inquiry",
      body: "Account/Invoice Number:\n\nPlease assist with my bill payment.\n",
    }),
  buttonInput: true,
  buttonInputPlaceholder: "Id Number / Email",
  footerText:
    "Supported by real-time billing, 24/7 emergency response, and secure systems.",
  footerButtonText: "Learn More",
  footerButtonOnclick: () =>
    openMailto({ subject: "Billing Information Request" }),
  footerIcon: <img src={safeIcon} alt="" className="w-7 h-7" />,
};

const paymentMethods = [
  {
    title: "Online Card Payment",
    icon: paymentCardIcon,
    info: "VISA, MasterCard, Apple Pay",
  },
  { title: "Bank Transfer", icon: bankIcon },
  { title: "Mobile App Payment", icon: mobileIcon },
  { title: "Auto-Debit", icon: autoDebitIcon },
];

function PayBill() {
  return (
    <div className="bg-white">
      <Hero data={heroData} />

      {/* HOW IT WORKS */}
      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10 bg-[#F6F6F9]">
        <div className="w-[1368px] max-w-full mx-auto">
          <div className="grid gap-5 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {payBillSteps.map((step, index) => (
              <article
                key={index}
                className="p-5 md:p-8 bg-white rounded-[12px] md:rounded-[20px]"
              >
                <div className="bg-black size-10 md:size-[56px] grid place-content-center rounded-full">
                  <span className="text-sm font-semibold text-white md:text-base">
                    0{index + 1}
                  </span>
                </div>

                <h3 className="mt-4 text-[#1C1C1C] text-lg md:text-xl font-semibold">
                  {step.title}
                </h3>

                <p className="mt-3 text-[#0A0E2EBF] text-[14px] md:text-sm lg:text-base">
                  {step.desc}
                </p>

                <button className="flex items-center gap-2 mt-6 text-xs md:text-lg font-medium text-[#0A0E2E] hover:underline">
                  {step.link}
                  <img src={arrowRightIcon} alt="arrow icon" />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* UNDERSTANDING BILL */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col lg:flex-row justify-between items-start gap-10">
          <div className="lg:max-w-[700px]">
            <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue">
              Understanding Your Gas Bill
            </h2>

            <p className="mt-4 text-[#62636C] text-sm md:text-base">
              Each GAZ bill shows: Billing Period and meter reading (start to
              end), Consumption in m³ or Kg, Tariff rate applied, VAT (5%) and
              any adjustments, Due Date and Total Amount Payable.
            </p>
          </div>

          <Button
            title="Download Sample Bill"
            onClick={() =>
              openMailto({
                subject: "Request: Sample Bill",
                body: "Hello,\n\nI would like to receive a sample bill document.\n\nThank you.",
              })
            }
          />
        </div>
      </section>

      {/* MULTIPLE WAYS TO PAY */}
      <section className="py-10 md:py-20 border-t border-[#B9BBC6] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue">
            Multiple Ways to Pay
          </h2>

          <div className="grid gap-5 mt-8 md:gap-10 md:mt-12 md:grid-cols-2 lg:grid-cols-4">
            {paymentMethods.map((method, index) => (
              <article
                key={index}
                className="p-6 bg-[#F6F6F9] rounded-md md:rounded-lg"
              >
                <img
                  src={method.icon}
                  alt={method.title}
                  className="size-10 md:size-20"
                />
                <h4 className="mt-4 md:mt-10 text-[#090D2C] text-xs md:text-base lg:text-lg font-normal">
                  {method.title}
                </h4>
                {method.info && (
                  <span className="text-[#090D2C] text-[14px] md:text-sm">
                    {method.info}
                  </span>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* REFUNDS */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col lg:flex-row justify-between gap-20">
          <div className="">
            <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue">
              Refunds & Deposits
            </h2>

            <p className="mt-4 text-[#62636C] text-sm md:text-base lg:text-2xl">
              Refunds on deposits or overpayments are processed within 7–10
              working days. For refund requests, please contact our billing team
              at help@gazglobal.net with your account details and proof of payment.
            </p>
          </div>

          <img src={billImage} alt="sample bill" className="sm:w-[356px]" />
        </div>
      </section>

      {/* HELP SECTION */}
      <Help />
    </div>
  );
}

export default PayBill;
